import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ListFilter, Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useLocation } from "wouter";
import CommonGrid from "@/components/pos/admin/common/grid";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import AdvanceFiltersModal, { type FilterConfig } from "@/components/pos/admin/common/advance-filters-modal";
import ActionTemplate from "@/components/pos/admin/common/action-template";
import { useDebounce } from "@/hooks/pos/use-debounce";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import ConfirmModal from "@/components/pos/admin/common/confirmation-modal";
import { NameAvatar } from "@/components/pos/admin/common/avatar";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { posApiFetch } from "@/utils/pos-api";
import { getStatusColorCode, getStatusDotColor } from "@/lib/pos/global";
import { useToast } from "@/hooks/use-toast";

const fetchSubcategories = async () => {
  const res = await posApiFetch("/v1/pos/subcategories");
  if (!res.ok) throw new Error("Failed to fetch subcategories");
  return res.json();
};

const fetchProducts = async () => {
  const res = await posApiFetch("/v1/pos/products");
  if (!res.ok) throw new Error("Failed to fetch products");
  return res.json();
};

type SubcategoriesState = {
  status?: string;
  currentPage?: number;
  itemsPerPage?: number;
  searchQuery?: string;
  dateRange?: { from: string; to: string };
};

const defaultSubcategoriesState: SubcategoriesState = {
  status: "all",
  currentPage: 1,
  itemsPerPage: 10,
};

const normalizeDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    if (!value.trim()) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

export default function SubcategoriesPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<any>(null);
  const [editForm, setEditForm] = useState({ name: "", description: "", active: true });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => { document.title = "Subcategories | SECH Admin"; }, []);

  const [subcategoriesState, setSubcategoriesState] = usePagePersistentState<SubcategoriesState>("subcategories", defaultSubcategoriesState);
  const searchQuery = subcategoriesState.searchQuery;
  const currentPage = subcategoriesState.currentPage;
  const itemsPerPage = subcategoriesState.itemsPerPage;
  const statusFilter = subcategoriesState.status;
  const dateRange = subcategoriesState.dateRange;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "subcategories_table",
    defaultColumn: "name",
    defaultDirection: "asc",
  });

  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    status: "", dateRange: { from: "", to: "" },
  });

  useEffect(() => {
    setFilterValues((prev) => ({
      ...prev,
      status: statusFilter ?? "",
      dateRange: { from: dateRange?.from ?? "", to: dateRange?.to ?? "" },
    }));
  }, [statusFilter, dateRange]);

  const handleApplyFilters = (values: Record<string, any>) => {
    const fromDate = normalizeDate(values.dateRange?.from);
    const toDate = normalizeDate(values.dateRange?.to);
    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      toast({ title: "Invalid filters", description: '"From" date cannot be after "To" date', variant: "destructive" });
      return;
    }
    setFilterValues(values);
    setSubcategoriesState((prev) => ({
      ...prev,
      status: values.status || "all",
      currentPage: 1,
      dateRange: { from: values.dateRange?.from ?? "", to: values.dateRange?.to ?? "" },
    }));
  };

  const { data: subcategories = [], isLoading, error } = useQuery({
    queryKey: ["/v1/pos/subcategories"],
    queryFn: fetchSubcategories,
    retry: 3,
    retryDelay: 1000,
  });

  const { data: products = [] } = useQuery({
    queryKey: ["/v1/pos/products"],
    queryFn: fetchProducts,
    retry: 3,
    retryDelay: 1000,
  });

  const productCountBySubcategory = useMemo(() => {
    const map: Record<number, number> = {};
    products.forEach((p: any) => {
      const subId = p.subcategoryId ?? p.subcategory?.id;
      if (subId) map[subId] = (map[subId] || 0) + 1;
    });
    return map;
  }, [products]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await posApiFetch(`/v1/pos/subcategories/${deleteTarget.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete subcategory");
      toast({ title: "Subcategory deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/subcategories"] });
    } catch {
      toast({ title: "Error", description: "Failed to delete subcategory", variant: "destructive" });
    } finally {
      setDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  // Populate edit form when editingRow changes
  useEffect(() => {
    if (editingRow && editingRow.id) {
      setEditForm({ name: editingRow.name ?? "", description: editingRow.description ?? "", active: editingRow.active ?? true });
    } else if (editingRow) {
      setEditForm({ name: "", description: "", active: true });
    }
  }, [editingRow]);

  const handleSaveSubcategory = async () => {
    if (!editForm.name.trim()) {
      toast({ title: "Validation Error", description: "Name is required", variant: "destructive" });
      return;
    }
    setIsSaving(true);
    try {
      const isEdit = editingRow?.id;
      const url = isEdit ? `/v1/pos/subcategories/${editingRow.id}` : "/v1/pos/subcategories";
      const method = isEdit ? "PUT" : "POST";
      const res = await posApiFetch(url, {
        method,
        body: JSON.stringify({
          name: editForm.name.trim(),
          description: editForm.description.trim() || "",
          active: editForm.active,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || `Failed to ${isEdit ? "update" : "create"} subcategory`);
      }
      toast({ title: `Subcategory ${isEdit ? "updated" : "created"} successfully` });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/subcategories"] });
      setEditingRow(null);
    } catch (err: any) {
      toast({ title: "Error", description: err?.message || "Failed to save subcategory", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const getFilterCount = () => {
    let count = 0;
    if (subcategoriesState.status && subcategoriesState.status !== "all") count += 1;
    const fromDate = normalizeDate(subcategoriesState.dateRange?.from);
    const toDate = normalizeDate(subcategoriesState.dateRange?.to);
    if (fromDate || toDate) count += 1;
    return count;
  };

  const statusOptions = [
    { label: "All", value: "all" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const subcategoryFilters: FilterConfig[] = useMemo(() => [
    { key: "status", label: "Status", type: "select", options: statusOptions, placeholder: "Select Option" },
    { key: "dateRange", label: "Date Range", type: "dateRange" },
  ], []);

  const handleClear = () => {
    setFilterValues({ status: "all", dateRange: { from: "", to: "" } });
    setSubcategoriesState((prev) => ({ ...prev, status: "all", dateRange: { from: "", to: "" }, currentPage: 1 }));
  };

  const setTableColumns = useMemo(() => [
    {
      label: "Name", field: "name", sortable: true,
      cellTemplate: (value: string, row: any) => (
        <div className="flex items-center gap-2">
          <NameAvatar name={value || ""} imageUrl={row?.imageUrl} />
          <span className="text-[13px] text-[#505258]">{value}</span>
        </div>
      ),
    },
    {
      label: "Status", field: "active", sortable: true,
      cellTemplate: (value: any) => {
        const status = value ? "active" : "inactive";
        return (
          <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${getStatusColorCode(status)}`}>
            <span className={`h-2 w-2 rounded-full mr-1 ${getStatusDotColor(status)} inline-flex items-center justify-center`} />
            <span className="leading-5 text-[13px] font-normal">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
          </span>
        );
      },
    },
    {
      label: "Display Order", field: "displayOrder", sortable: true,
      cellTemplate: (value: any) => <span className="text-[13px] text-[var(--secondary-text-color)]">{value ?? "-"}</span>,
    },
    {
      label: "Products", field: "id",
      cellTemplate: (value: any) => <span className="text-[13px] text-[var(--secondary-text-color)]">{productCountBySubcategory[value] ?? 0}</span>,
    },
    {
      label: "Date & Time", field: "createdAt", sortable: true,
      cellTemplate: (value: string) => formatDate(value),
    },
    {
      label: "Action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: any, row: any) => (
        <ActionTemplate
          row={row}
          actions={[
            { label: "Edit", icon: Pencil, onClick: (r: any) => setEditingRow(r) },
            { label: "Delete", icon: Trash2, onClick: (r: any) => { setDeleteTarget(r); setDeleteModal(true); }, destructive: true },
          ]}
        />
      ),
    },
  ], [productCountBySubcategory]);

  const filteredSubcategories = useMemo(() =>
    subcategories.filter((sub: any) => {
      const search = debouncedSearch?.trim().toLowerCase() ?? "";
      const status = sub.active ? "active" : "inactive";
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const matchesSearch = !search || sub.name?.toLowerCase().includes(search) || sub.displayName?.toLowerCase().includes(search) || sub.category?.toLowerCase().includes(search);
      const fromDate = normalizeDate(subcategoriesState.dateRange?.from);
      const toDate = normalizeDate(subcategoriesState.dateRange?.to);
      const createdAt = new Date(sub.createdAt).getTime();
      const matchesDateRange = fromDate && toDate ? createdAt >= fromDate.getTime() && createdAt <= toDate.getTime() : true;
      return matchesStatus && matchesSearch && matchesDateRange;
    }), [subcategories, statusFilter, debouncedSearch, subcategoriesState.dateRange]
  );

  const sortedSubcategories = useMemo(() => {
    return [...filteredSubcategories].sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "name": aVal = a?.name ?? ""; bVal = b?.name ?? ""; break;
        case "active": aVal = a?.active ? 1 : 0; bVal = b?.active ? 1 : 0; break;
        case "displayOrder": aVal = a?.displayOrder ?? 0; bVal = b?.displayOrder ?? 0; break;
        case "createdAt": aVal = a?.createdAt ? new Date(a.createdAt).getTime() : 0; bVal = b?.createdAt ? new Date(b.createdAt).getTime() : 0; break;
        default: aVal = a?.name ?? ""; bVal = b?.name ?? "";
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredSubcategories, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedSubcategories.length / (itemsPerPage ?? 10));
  const startIndex = ((currentPage ?? 1) - 1) * (itemsPerPage ?? 10);
  const paginatedSubcategories = sortedSubcategories.slice(startIndex, startIndex + (itemsPerPage ?? 10));

  const handlePageChange = (page: number) => { setSubcategoriesState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setSubcategoriesState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems: sortedSubcategories.length, handlePageChange, handleItemsPerPageChange };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md"><CardContent className="p-6">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Subcategories</h2>
          <p className="text-gray-600 mb-4">Failed to load subcategories</p>
          <Button onClick={() => window.location.reload()} className="w-full">Reload Page</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">Subcategories</p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className={`flex h-8 items-center px-1 p-2 rounded-sm ${getFilterCount() > 0 ? "bg-[var(--secondary-background-color)]" : ""}`}>
            <Button onClick={() => setIsFilterOpen(true)} variant="ghost" className={`px-2 text-[var(--primary-text-color)] flex items-center text-[13px] font-medium leading-[15px] justify-center gap-2 rounded-[4px] ${getFilterCount() > 0 ? "h-6 hover:bg-[var(--primary-background-hover-color)]" : "h-8"}`}>
              <ListFilter className="h-4 w-4" /> Filter
              {getFilterCount() > 0 && <span className="text-[13px] bg-[var(--primary-color)] h-4 w-4 flex items-center justify-center rounded-full leading-[15px] font-medium text-white">{getFilterCount()}</span>}
            </Button>
            {getFilterCount() > 0 && (
              <>
                <span className="text-[13px] text-[var(--secondary-text-color)] px-1">|</span>
                <Button type="button" onClick={handleClear} className="text-[13px] text-[var(--primary-text-color)] font-medium px-2 h-6 hover:bg-[var(--primary-background-hover-color)] bg-transparent">Clear</Button>
              </>
            )}
          </div>
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center" type="text" placeholder="Search" value={searchQuery ?? ""} onChange={(e) => setSubcategoriesState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))} />
          </div>
          <Button onClick={() => setEditingRow({})} className="h-[32px] px-3 text-[13px] bg-[var(--primary-color)] text-white rounded-[4px] hover:bg-[#9f0e0e] flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add Subcategory
          </Button>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="pt-0 h-[calc(100vh-130px)]">
          {!paginatedSubcategories.length && !isLoading ? (
            <NoRecordFound title="Subcategories" description="There are no subcategories available." action={
              <Button onClick={() => setEditingRow({})} className="h-[32px] px-3 text-[13px] bg-[var(--primary-color)] text-white rounded-[4px] hover:bg-[#9f0e0e] flex items-center gap-1">
                <Plus className="h-4 w-4" /> Add Subcategory
              </Button>
            } />
          ) : (
            <CommonGrid
              rowData={paginatedSubcategories}
              isLoading={isLoading}
              columns={setTableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>
      <AdvanceFiltersModal isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filter" filters={subcategoryFilters} initialValues={filterValues} onApply={handleApplyFilters} onClearAll={handleClear} />
      <ConfirmModal open={deleteModal} onClose={() => { setDeleteModal(false); setDeleteTarget(null); }} onConfirm={handleDelete} title="Delete Subcategory" description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`} confirmText="Delete" variant="delete" />

      {/* Add / Edit Subcategory Dialog */}
      <Dialog open={!!editingRow} onOpenChange={(open) => { if (!open) setEditingRow(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRow?.id ? "Edit Subcategory" : "Add Subcategory"}</DialogTitle>
            <DialogDescription>
              {editingRow?.id ? "Update the subcategory details below." : "Fill in the details to create a new subcategory."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label className="text-[12px]">Name *</Label>
              <Input
                className="h-[36px] text-[13px]"
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Enter subcategory name"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[12px]">Description</Label>
              <Textarea
                className="text-[13px] min-h-[80px]"
                value={editForm.description}
                onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Subcategory description"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={editForm.active} onCheckedChange={(v) => setEditForm((prev) => ({ ...prev, active: v }))} />
              <Label className="text-[12px]">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="h-[36px] text-[13px]" onClick={() => setEditingRow(null)} disabled={isSaving}>Cancel</Button>
            <Button type="button" className="h-[36px] text-[13px] bg-[var(--pos-primary-color)] text-white hover:bg-[#9f0e0e]" onClick={handleSaveSubcategory} disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {editingRow?.id ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
